/**
 * Option Entity Builder
 * Creates Excel EntityCellValue objects for option contracts with flat scalar properties
 */

/* global Excel */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { OPTION_ENTITY_V1_FIELDS, isV1Field, getOptionEntityV1Fields } from "./optionEntity.schema";
import { logger } from "../../shared/logger";
import { toExcelDate } from "../../shared/datetime";

/**
 * Option Entity Properties
 * Flat scalar properties for Excel column extraction
 * Property names are locked and must not change (v1 schema)
 * 
 * NOTE: Metadata fields (request_id, fetched_at, source, source_note, as_of) are
 * NOT included in this schema. They are used for diagnostics/logging only and
 * may appear in card display but not in entity properties.
 */
export interface OptionEntityProps {
  // Core identity
  underlying: string;
  expiration: string;        // YYYY-MM-DD
  strike: number;
  right: "C" | "P";
  display_name: string;
  occ_symbol?: string;
  
  // Quote
  bid?: number;
  ask?: number;
  mid?: number;
  last?: number;
  change?: number;
  change_percent?: number;   // Locked name (not change_pct)
  volume?: number;
  open_interest?: number;    // Locked name
  
  // Greeks
  iv?: number;
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  rho?: number;
  
  // Timestamp
  updatedAt?: string; // ISO date-time string
}

/**
 * Sanitize numeric values - filter NaN and Infinity
 * Prevents #VALUE! errors in Excel
 */
function safeNumber(value?: number): number | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  return Number.isFinite(value) ? value : undefined;
}

/**
 * Format property key to display name (title case)
 * Converts snake_case to Title Case, handles special cases
 */
function formatPropertyDisplayName(key: string): string {
  // Special cases with custom formatting
  const specialCases: Record<string, string> = {
    "change_percent": "Change (%)",
    "open_interest": "Open Interest",
    "occ_symbol": "OCC Symbol",
    "display_name": "Display Name",
    "as_of": "As Of",
    "iv": "IV", // Implied Volatility - commonly shown as "IV"
  };
  
  if (specialCases[key]) {
    return specialCases[key];
  }
  
  // Convert snake_case to Title Case
  return key
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Get the canonical Excel property name for a field key
 * Handles special cases and accepts both camelCase and snake_case variants
 * @param fieldKey - Field key from schema (e.g., "open_interest", "updatedAt", "updated_at")
 * @returns Canonical Excel property name (e.g., "Open Interest", "UpdatedAt")
 */
export function getOptionPropertyName(fieldKey: string): string {
  // Handle updatedAt/updated_at special case (both map to "UpdatedAt")
  if (fieldKey === "updatedAt" || fieldKey === "updated_at") {
    return "UpdatedAt";
  }
  
  // Use existing formatPropertyDisplayName logic
  return formatPropertyDisplayName(fieldKey);
}

/**
 * Ensure all canonical schema property keys exist; fill missing with blank ("").
 * Prevents #FIELD! when chain formulas reference properties that upstream omits
 * (e.g. illiquid contracts missing last/change). Only adds keys that are absent;
 * does not overwrite real values including 0 or false.
 * @param properties - Mutable properties map (modified in place)
 * @returns The same object (for chaining)
 */
export function fillMissingOptionProperties(properties: Record<string, any>): Record<string, any> {
  const fields = getOptionEntityV1Fields();
  for (const fieldKey of fields) {
    const propertyName = getOptionPropertyName(fieldKey);
    if (!(propertyName in properties)) {
      properties[propertyName] = cvString("");
    }
  }
  return properties;
}

/**
 * Reverse map display name back to schema field name
 * Used for validation to check if a property key is in the v1 schema
 * @deprecated Use ALLOWED_DISPLAY_KEYS for validation instead
 */
function displayNameToFieldName(displayName: string): string {
  // Reverse mapping for special cases
  const reverseMap: Record<string, string> = {
    "Change (%)": "change_percent",
    "Open Interest": "open_interest",
    "OCC Symbol": "occ_symbol",
    "Display Name": "display_name",
    "As Of": "as_of",
    "IV": "iv",
  };
  
  if (reverseMap[displayName]) {
    return reverseMap[displayName];
  }
  
  // Convert Title Case back to snake_case
  return displayName
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[()%]/g, "");
}

/**
 * Generate allowlist of valid display keys from schema fields + hardcoded aliases
 * Used for dev-only validation to ensure only expected display keys are emitted
 */
function getAllowedDisplayKeys(): Set<string> {
  const allowed = new Set<string>();
  
  // Add display keys from all schema fields
  const schemaFields = getOptionEntityV1Fields();
  for (const field of schemaFields) {
    allowed.add(formatPropertyDisplayName(field));
  }
  
  // Add hardcoded aliases (not generated from schema fields)
  allowed.add("OI"); // Alias for open_interest
  allowed.add("Open Interest"); // Canonical for open_interest (already added above, but explicit)
  allowed.add("UpdatedAt"); // Canonical for updatedAt (Excel datetime serial)
  allowed.add("UpdatedAtUtc"); // Companion for updatedAt (ISO string)
  
  return allowed;
}

/**
 * Dev-only: Validate that all property keys are in the allowed display keys set
 * This ensures we don't accidentally emit unexpected fields
 */
function validatePropertyKeys(properties: Record<string, unknown>): void {
  if (process.env.NODE_ENV === "production") {
    return; // Skip validation in production
  }
  
  const allowedKeys = getAllowedDisplayKeys();
  const actualKeys = Object.keys(properties);
  
  for (const key of actualKeys) {
    if (!allowedKeys.has(key)) {
      logger.log("optionEntity", "WARNING: Unexpected property key in entity: " + key + " (not in allowed display keys)");
    }
  }
}

/**
 * Entity Value Helpers
 * Abstract wrapper format so it can be adjusted if Excel rejects the structure
 */

function cvString(value?: string, displayName?: string): any {
  if (value === undefined || value === null) {
    return undefined;
  }
  // Excel.CellValueType enum values are strings: "String", "Double", "Boolean", "Entity"
  // Try using Excel API enum if available, otherwise use string literal
  let stringType: any = "String";
  try {
    if (typeof Excel !== "undefined" && Excel.CellValueType) {
      stringType = Excel.CellValueType.string;
    }
  } catch {
    // Excel API not available in custom functions context
  }
  
  const result: any = { type: stringType, basicValue: value };
  
  // Excel shows property keys in card view. Since we use snake_case keys (locked schema),
  // Excel displays them as-is. The sublabel is additional text, not a replacement.
  // Unfortunately, there's no way to hide the property key and only show a friendly name
  // while keeping snake_case keys for programmatic access.
  // 
  // However, we can try using the sublabel as the primary display by not setting it,
  // or by using a workaround. For now, we'll remove sublabel to see if Excel
  // automatically formats the property key better, or we may need to accept the
  // "key • sublabel" format as Excel's behavior.
  
  return result;
}

/**
 * Create a number cell value with optional number format for card display
 * @param value - Numeric value
 * @param numberFormat - Optional Excel number format string (e.g., "$#,##0.00", "#,##0")
 * @returns Cell value object with type "Double" and basicValue as number
 */
function cvNumber(value?: number, numberFormat?: string): any {
  if (value === undefined || value === null) {
    return undefined;
  }
  // Excel.CellValueType.double = "Double" (string)
  let doubleType: any = "Double";
  try {
    if (typeof Excel !== "undefined" && Excel.CellValueType) {
      doubleType = Excel.CellValueType.double;
    }
  } catch {
    // Excel API not available
  }
  
  const result: any = { type: doubleType, basicValue: value };
  
  // Add numberFormat if provided - this formats the display in the card
  // while basicValue remains a number for formulas
  if (numberFormat !== undefined) {
    result.numberFormat = numberFormat;
  }
  
  return result;
}

/**
 * Format number as currency string for entity card display
 * Uses $x.xx format (2 decimals) unless value has more precision, then uses $x.xxxx (up to 4 decimals)
 */
function cvCurrency(value?: number): any {
  if (value === undefined || value === null) {
    return undefined;
  }
  
  // Determine decimal places: if value has more than 2 decimal places of precision, show up to 4; otherwise show 2
  // Check if rounding to 2 decimals loses precision (i.e., value has more than 2 decimal places)
  const roundedTo2 = Math.round(value * 100) / 100;
  const roundedTo4 = Math.round(value * 10000) / 10000;
  const hasMorePrecision = Math.abs(value - roundedTo2) > 0.0001; // Value has more than 2 decimal places
  const decimals = hasMorePrecision ? 4 : 2;
  
  // Format as currency string using the appropriate precision
  const formatted = "$" + roundedTo4.toFixed(decimals);
  
  // Return as String type for entity card
  let stringType: any = "String";
  try {
    if (typeof Excel !== "undefined" && Excel.CellValueType) {
      stringType = Excel.CellValueType.string;
    }
  } catch {
    // Excel API not available
  }
  
  return { type: stringType, basicValue: formatted };
}

/**
 * Format number with 4 decimal places and comma separators for thousands
 * Used for Greeks and other numeric values that need precise display
 * Examples: 0.1234, 1,234.5678, 123,456.7890
 */
function cvNumberFormatted(value?: number): any {
  if (value === undefined || value === null) {
    return undefined;
  }
  
  // Round to 4 decimal places
  const rounded = Math.round(value * 10000) / 10000;
  
  // Split into integer and decimal parts
  const parts = rounded.toFixed(4).split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1];
  
  // Add comma separators to integer part (for values >= 1000)
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  
  // Combine: integer with commas + decimal part
  const formatted = formattedInteger + "." + decimalPart;
  
  // Return as String type for entity card
  let stringType: any = "String";
  try {
    if (typeof Excel !== "undefined" && Excel.CellValueType) {
      stringType = Excel.CellValueType.string;
    }
  } catch {
    // Excel API not available
  }
  
  return { type: stringType, basicValue: formatted };
}

/**
 * Format integer with comma separators for thousands (no decimals)
 * Used for volume, open interest, and other whole number counts
 * Examples: 1,234, 123,456, 1,234,567
 */
function cvIntegerFormatted(value?: number): any {
  if (value === undefined || value === null) {
    return undefined;
  }
  
  // Round to nearest integer
  const rounded = Math.round(value);
  
  // Add comma separators
  const formatted = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  
  // Return as String type for entity card
  let stringType: any = "String";
  try {
    if (typeof Excel !== "undefined" && Excel.CellValueType) {
      stringType = Excel.CellValueType.string;
    }
  } catch {
    // Excel API not available
  }
  
  return { type: stringType, basicValue: formatted };
}

function cvBoolean(value?: boolean): any {
  if (value === undefined || value === null) {
    return undefined;
  }
  // Excel.CellValueType.boolean = "Boolean" (string)
  try {
    if (typeof Excel !== "undefined" && Excel.CellValueType) {
      return { type: Excel.CellValueType.boolean, basicValue: value };
    }
  } catch {
    // Excel API not available
  }
  return { type: "Boolean", basicValue: value };
}

function cvDateIso(value?: string): any {
  if (value === undefined || value === null) {
    return undefined;
  }
  // ISO dates as strings for v1
  return { type: "String", basicValue: value };
}

/**
 * Convert ISO-8601 UTC string to Excel datetime serial (1900 date system)
 * Converts UTC to local time first, then to Excel serial
 * Excel serial numbers are timezone-agnostic - they represent absolute moments in time
 * When Excel displays them, it shows them in the user's local timezone
 * 
 * Strategy: We want Excel to display the local time equivalent of the UTC timestamp.
 * To do this, we need to store the serial that represents the UTC moment directly.
 * Excel will then automatically display it in local time.
 * 
 * Example: "2026-01-26T05:00:00.000Z" (5 AM UTC) should display as "1/26/2026 00:00:00" in EST (UTC-5)
 * We store the serial for 5 AM UTC, and Excel displays it as midnight EST.
 * 
 * @param isoUtc - ISO-8601 UTC string (e.g., "2026-01-26T05:00:00.000Z")
 * @returns Excel serial number (days since 1899-12-30) or undefined if invalid
 */
function isoUtcToExcelSerialLocal(isoUtc: string): number | undefined {
  // Parse UTC string to get UTC milliseconds
  const utcMs = Date.parse(isoUtc);
  if (!Number.isFinite(utcMs)) return undefined;

  // Excel serial numbers are timezone-agnostic - they represent absolute moments in time
  // When Excel displays a serial, it automatically converts to the user's local timezone
  // So we just need to convert the UTC timestamp directly to Excel serial
  // Excel will handle the timezone conversion for display
  
  // Excel 1900 date system: days since 1899-12-30
  // Convert UTC milliseconds directly to Excel serial
  return utcMs / 86400000 + 25569;
}

/**
 * Format Excel datetime serial as date/time string for entity card display
 * Formats as "M/D/YYYY HH:MM:SS" (e.g., "1/26/2026 00:00:00")
 * Uses local time components to match what Excel will display
 * @param serial - Excel datetime serial number (represents UTC moment)
 * @returns Formatted date/time string in local timezone
 */
function formatExcelSerialAsDateTime(serial: number): string {
  // Convert Excel serial to JavaScript Date
  // Excel serial: days since 1899-12-30 (represents UTC moment)
  // JavaScript Date: milliseconds since 1970-01-01
  // Round to nearest millisecond to avoid floating point precision issues
  const ms = Math.round((serial - 25569) * 86400000);
  const jsDate = new Date(ms);
  
  // Use local time components (getMonth, getDate, etc.) to match what Excel displays
  // Excel automatically converts the UTC serial to local time for display
  const month = jsDate.getMonth() + 1;
  const day = jsDate.getDate();
  const year = jsDate.getFullYear();
  const hours = jsDate.getHours(); // Local hours
  const minutes = jsDate.getMinutes(); // Local minutes
  const seconds = jsDate.getSeconds(); // Local seconds
  
  // Pad with zeros
  const hoursStr = hours.toString().padStart(2, "0");
  const minutesStr = minutes.toString().padStart(2, "0");
  const secondsStr = seconds.toString().padStart(2, "0");
  
  return `${month}/${day}/${year} ${hoursStr}:${minutesStr}:${secondsStr}`;
}

/**
 * Format Excel datetime serial as date/time for entity card
 * Returns a formatted string for display while keeping the underlying serial as a number
 * Note: This returns a string type for entity card display, but formulas will still work
 * because they reference the property key, not the display value
 * @param serial - Excel datetime serial number
 * @returns Entity value with formatted date/time string
 */
function cvDateTime(serial?: number): any {
  if (serial === undefined || serial === null) {
    return undefined;
  }
  
  // Format the serial as a readable date/time string
  const formatted = formatExcelSerialAsDateTime(serial);
  
  // Return as String type for entity card display
  // The underlying value in Excel is still the serial number for formulas
  let stringType: any = "String";
  try {
    if (typeof Excel !== "undefined" && Excel.CellValueType) {
      stringType = Excel.CellValueType.string;
    }
  } catch {
    // Excel API not available
  }
  
  return { type: stringType, basicValue: formatted };
}

/**
 * Test override: Set to false to force List layout (for testing backward compatibility)
 * This is only used in test environments and should not be set in production code
 */
let _forceListLayout: boolean | undefined = undefined;

/**
 * Set forced layout mode for testing (forces List layout when true)
 * @param force - If true, forces List layout regardless of ExcelApi version
 * @internal - For testing only
 */
export function setForceListLayoutForTesting(force: boolean | undefined): void {
  _forceListLayout = force;
}

/**
 * Check if ExcelApi 1.19 is supported (for TwoColumn layout)
 * Returns false if Office is not available (e.g., in tests)
 * Can be overridden for testing via setForceListLayoutForTesting()
 */
export function isTwoColumnLayoutSupported(): boolean {
  // Test override: if explicitly set, use that value
  if (_forceListLayout !== undefined) {
    return !_forceListLayout; // Return false if forcing List, true if forcing TwoColumn
  }
  
  try {
    if (typeof Office !== "undefined" && Office.context?.requirements) {
      return Office.context.requirements.isSetSupported("ExcelApi", "1.19");
    }
  } catch {
    // Office not available or requirements check failed
  }
  return false;
}

/**
 * Build card layout
 * Matches Office.js entity card layout format (validated via smoke test)
 * Uses TwoColumn layout for richer display if ExcelApi 1.19+ is available,
 * otherwise falls back to List layout for backward compatibility
 */
function buildCardLayout(props: OptionEntityProps): any {
  // Check if TwoColumn layout is supported (ExcelApi 1.19+)
  const supportsTwoColumn = isTwoColumnLayoutSupported();
  
  // IMPORTANT: These `properties` entries MUST match the keys we place into `entity.properties`.
  // In this file, keys are `formatPropertyDisplayName("field")` (Title Case / special-cased labels).

  const sections: any[] = [];
  
  // ---- Quote + Stats (TwoColumn) ----
  // TwoColumn layout automatically splits the properties array into two columns
  const quoteStatsProps: string[] = [];
  
  // Core quote fields (will appear in left column)
  if (props.bid !== undefined) quoteStatsProps.push(formatPropertyDisplayName("bid"));
  if (props.ask !== undefined) quoteStatsProps.push(formatPropertyDisplayName("ask"));
  if (props.mid !== undefined) quoteStatsProps.push(formatPropertyDisplayName("mid"));
  if (props.last !== undefined) quoteStatsProps.push(formatPropertyDisplayName("last"));
  
  // Change + activity fields (will appear in right column)
  if (props.change !== undefined) quoteStatsProps.push(formatPropertyDisplayName("change"));
  if (props.change_percent !== undefined) quoteStatsProps.push(formatPropertyDisplayName("change_percent"));
  if (props.volume !== undefined) quoteStatsProps.push(formatPropertyDisplayName("volume"));
  if (props.open_interest !== undefined) quoteStatsProps.push(formatPropertyDisplayName("open_interest"));

  if (quoteStatsProps.length > 0) {
    sections.push({
      layout: supportsTwoColumn ? "TwoColumn" : "List",
      title: "Quote & Stats",
      collapsible: true,
      collapsed: false, // Expanded by default
      properties: quoteStatsProps,
    });
  }

  // ---- Greeks ----
  const greeksProps: string[] = [];
  
  if (props.iv !== undefined) greeksProps.push(formatPropertyDisplayName("iv"));
  if (props.delta !== undefined) greeksProps.push(formatPropertyDisplayName("delta"));
  if (props.gamma !== undefined) greeksProps.push(formatPropertyDisplayName("gamma"));
  if (props.theta !== undefined) greeksProps.push(formatPropertyDisplayName("theta"));
  if (props.vega !== undefined) greeksProps.push(formatPropertyDisplayName("vega"));
  if (props.rho !== undefined) greeksProps.push(formatPropertyDisplayName("rho"));

  if (greeksProps.length > 0) {
    sections.push({
      layout: supportsTwoColumn ? "TwoColumn" : "List",
      title: "Greeks",
      collapsible: true,
      collapsed: false, // Expanded by default
      properties: greeksProps,
    });
  }

  // ---- Contract Details ----
  const contractProps: string[] = [];
  
  // Order matters for TwoColumn: first half goes left, second half goes right
  // For List, order is preserved as-is
  if (props.underlying !== undefined) contractProps.push(formatPropertyDisplayName("underlying"));
  if (props.strike !== undefined) contractProps.push(formatPropertyDisplayName("strike"));
  if (props.right !== undefined) contractProps.push(formatPropertyDisplayName("right"));
  if (props.expiration !== undefined) contractProps.push(formatPropertyDisplayName("expiration"));
  if (props.occ_symbol !== undefined) contractProps.push(formatPropertyDisplayName("occ_symbol"));
  if (props.updatedAt !== undefined) contractProps.push("UpdatedAt"); // Use property key directly for card layout (Excel datetime serial)

  if (contractProps.length > 0) {
    sections.push({
      layout: supportsTwoColumn ? "TwoColumn" : "List",
      title: "Contract Details",
      collapsible: true,
      collapsed: false, // Expanded by default
      properties: contractProps,
    });
  }
  
  // Fallback: if somehow no sections were added, show core identity in a simple list
  if (sections.length === 0) {
    sections.push({
      layout: "List",
      title: "Contract",
      properties: [
        formatPropertyDisplayName("underlying"),
        formatPropertyDisplayName("expiration"),
        formatPropertyDisplayName("strike"),
        formatPropertyDisplayName("right"),
      ],
    });
  }

  return {
    title: { property: formatPropertyDisplayName("display_name") },
    sections: sections,
  };
}

/**
 * Build option entity cell value
 * Creates Excel EntityCellValue with flat scalar properties
 */
export function buildOptionEntityCellValue(props: OptionEntityProps): any {
  // Sanitize numeric values first
  const safeStrike = safeNumber(props.strike);
  const safeBid = safeNumber(props.bid);
  const safeAsk = safeNumber(props.ask);
  const safeMid = safeNumber(props.mid);
  const safeLast = safeNumber(props.last);
  const safeChange = safeNumber(props.change);
  const safeChangePercent = safeNumber(props.change_percent);
  const safeVolume = safeNumber(props.volume);
  const safeOpenInterest = safeNumber(props.open_interest);
  const safeIv = safeNumber(props.iv);
  const safeDelta = safeNumber(props.delta);
  const safeGamma = safeNumber(props.gamma);
  const safeTheta = safeNumber(props.theta);
  const safeVega = safeNumber(props.vega);
  const safeRho = safeNumber(props.rho);
  
  // Compute mid if missing and bid/ask are finite numbers
  let computedMid = safeMid;
  if (computedMid === undefined && safeBid !== undefined && safeAsk !== undefined) {
    computedMid = (safeBid + safeAsk) / 2;
    // Sanitize computed mid
    computedMid = safeNumber(computedMid);
  }
  
  // Build properties object - explicitly map each v1 schema field
  // Do NOT dynamically spread objects - only emit known fields
  const properties: Record<string, any> = {};
  
  // Core identity (always present)
  properties[formatPropertyDisplayName("underlying")] = cvString(props.underlying);
  properties[formatPropertyDisplayName("expiration")] = cvString(props.expiration);
  if (safeStrike !== undefined) {
    // Store strike as number with currency format for card display
    // basicValue is number (for formulas), numberFormat controls card display
    properties[formatPropertyDisplayName("strike")] = cvNumber(safeStrike, "$#,##0.00");
  }
  properties[formatPropertyDisplayName("right")] = cvString(props.right);
  properties[formatPropertyDisplayName("display_name")] = cvString(props.display_name);
  if (props.occ_symbol !== undefined) {
    properties[formatPropertyDisplayName("occ_symbol")] = cvString(props.occ_symbol);
  }
  
  // Quote (optional) - only include v1 schema fields
  // Store as numbers with numberFormat for card display
  // basicValue is number (for formulas), numberFormat controls card display
  if (safeBid !== undefined) properties[formatPropertyDisplayName("bid")] = cvNumber(safeBid, "$#,##0.00");
  if (safeAsk !== undefined) properties[formatPropertyDisplayName("ask")] = cvNumber(safeAsk, "$#,##0.00");
  if (computedMid !== undefined) properties[formatPropertyDisplayName("mid")] = cvNumber(computedMid, "$#,##0.00");
  if (safeLast !== undefined) properties[formatPropertyDisplayName("last")] = cvNumber(safeLast, "$#,##0.00");
  if (safeChange !== undefined) properties[formatPropertyDisplayName("change")] = cvNumber(safeChange, "$#,##0.00");
  if (safeChangePercent !== undefined) properties[formatPropertyDisplayName("change_percent")] = cvNumber(safeChangePercent, "0.00%");
  // Volume and open interest: store as numbers with comma formatting for card display
  if (safeVolume !== undefined) properties[formatPropertyDisplayName("volume")] = cvNumber(safeVolume, "#,##0");
  if (safeOpenInterest !== undefined) {
    // Write canonical key "OI" (PascalCase, no spaces) for formulas
    // Store as number with format so =C3.OI returns a number, card shows formatted
    properties["OI"] = cvNumber(safeOpenInterest, "#,##0");
    // Also write compatibility alias "Open Interest" for existing workbooks
    properties["Open Interest"] = cvNumber(safeOpenInterest, "#,##0");
  }
  
  // Greeks (optional) - only include v1 schema fields
  // Store as numbers with format for card display
  // IV is stored as decimal (0.25 = 25%), format as percentage
  if (safeIv !== undefined) properties[formatPropertyDisplayName("iv")] = cvNumber(safeIv, "0.0000%");
  if (safeDelta !== undefined) properties[formatPropertyDisplayName("delta")] = cvNumber(safeDelta, "#,##0.0000");
  if (safeGamma !== undefined) properties[formatPropertyDisplayName("gamma")] = cvNumber(safeGamma, "#,##0.0000");
  if (safeTheta !== undefined) properties[formatPropertyDisplayName("theta")] = cvNumber(safeTheta, "#,##0.0000");
  if (safeVega !== undefined) properties[formatPropertyDisplayName("vega")] = cvNumber(safeVega, "#,##0.0000");
  if (safeRho !== undefined) properties[formatPropertyDisplayName("rho")] = cvNumber(safeRho, "#,##0.0000");
  
  // Timestamp (optional)
  if (props.updatedAt !== undefined) {
    // Canonical UTC string: ISO-8601 UTC (ends in Z)
    properties["UpdatedAtUtc"] = cvString(props.updatedAt);
    
    // Excel-local datetime: serial number (stored as number for formulas)
    // Format as date/time in card display (e.g., "1/26/26 04:37")
    const serial = isoUtcToExcelSerialLocal(props.updatedAt);
    if (serial !== undefined) {
      // Store as number (Excel datetime serial) with date/time format for card display
      // Format: m/d/yy hh:mm (e.g., "1/26/26 04:37")
      properties["UpdatedAt"] = cvNumber(serial, "m/d/yy hh:mm");
    }
  }
  
  // Guarantee all canonical schema keys exist (blank when missing) to avoid #FIELD! in chain formulas
  fillMissingOptionProperties(properties);
  
  // Schema enforcement: validate property keys against allowed display keys (dev-only)
  validatePropertyKeys(properties);
  
  // Metadata fields (request_id, fetched_at, source, source_note, as_of) are NOT included
  // in properties - they are used for diagnostics/logging only and may appear in
  // provider.description for card display
  
  // Filter out any undefined properties (defensive - should not happen but Excel may reject them)
  const filteredProperties: Record<string, any> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined && value !== null) {
      filteredProperties[key] = value;
    }
  }
  
  // Build entity
  // Excel.CellValueType.entity = "Entity" (string enum value)
  let entityType: any = "Entity"; // Default to string enum value
  try {
    if (typeof Excel !== "undefined" && Excel.CellValueType) {
      entityType = Excel.CellValueType.entity;
    }
  } catch {
    // Excel API not available, use string literal
  }
  
  // Build entity
  // Provider attribution can be shown in card via provider.description
  // (source_note passed separately for card display, not in properties)
  const entity = {
    type: entityType,
    text: props.display_name,
    properties: filteredProperties,
    layouts: {
      card: buildCardLayout(props),
    },
    // Provider attribution for card display (not a property)
    provider: {
      description: "Market data provided by Massive",
    },
  };
  
  return entity;
}
